import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface ProgramCheckoutState {
  // Session management
  sessionId: string | null;
  programId: string | null;
  programName: string | null;
  programPrice: number | null;

  // Discount state
  appliedDiscountCode: string | null;
  discountAmount: number;
  isDiscountApplied: boolean;

  // Admin discount (ROLE_SUPER_ADMIN only)
  isAdminDiscountApplied: boolean;
  adminDiscountType: "percentage" | "fixed_amount" | null;
  adminDiscountValue: number;
  adminDiscountAmount: number;
  adminDiscountReason: string;

  // Loading/error states
  loadingCreateSession: boolean;
  loadingProcessPayment: boolean;
  error: string | null;

  // Purchase result
  purchaseId: string | null;
  transactionId: string | null;
}

const initialState: ProgramCheckoutState = {
  sessionId: null,
  programId: null,
  programName: null,
  programPrice: null,
  appliedDiscountCode: null,
  discountAmount: 0,
  isDiscountApplied: false,
  isAdminDiscountApplied: false,
  adminDiscountType: null,
  adminDiscountValue: 0,
  adminDiscountAmount: 0,
  adminDiscountReason: "",
  loadingCreateSession: false,
  loadingProcessPayment: false,
  error: null,
  purchaseId: null,
  transactionId: null,
};

const programCheckoutSlice = createSlice({
  name: "programCheckout",
  initialState,
  reducers: {
    setSessionData(
      state,
      action: PayloadAction<{
        sessionId: string;
        programId: string;
        programName: string;
        programPrice: number;
      }>,
    ) {
      state.sessionId = action.payload.sessionId;
      state.programId = action.payload.programId;
      state.programName = action.payload.programName;
      state.programPrice = action.payload.programPrice;
    },
    setLoadingCreateSession(state, action: PayloadAction<boolean>) {
      state.loadingCreateSession = action.payload;
    },
    setLoadingProcessPayment(state, action: PayloadAction<boolean>) {
      state.loadingProcessPayment = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setDiscountApplied(
      state,
      action: PayloadAction<{
        discountCode: string;
        discountAmount: number;
      }>,
    ) {
      state.isDiscountApplied = true;
      state.appliedDiscountCode = action.payload.discountCode;
      state.discountAmount = action.payload.discountAmount;
    },
    removeDiscount(state) {
      state.isDiscountApplied = false;
      state.appliedDiscountCode = null;
      state.discountAmount = 0;
    },
    setAdminDiscountApplied(
      state,
      action: PayloadAction<{
        discountType: "percentage" | "fixed_amount";
        discountValue: number;
        discountAmount: number;
        reason: string;
      }>,
    ) {
      state.isAdminDiscountApplied = true;
      state.adminDiscountType = action.payload.discountType;
      state.adminDiscountValue = action.payload.discountValue;
      state.adminDiscountAmount = action.payload.discountAmount;
      state.adminDiscountReason = action.payload.reason;
    },
    removeAdminDiscount(state) {
      state.isAdminDiscountApplied = false;
      state.adminDiscountType = null;
      state.adminDiscountValue = 0;
      state.adminDiscountAmount = 0;
      state.adminDiscountReason = "";
    },
    setPurchaseCompleted(
      state,
      action: PayloadAction<{
        purchaseId: string;
        transactionId: string;
      }>,
    ) {
      state.purchaseId = action.payload.purchaseId;
      state.transactionId = action.payload.transactionId;
    },
    resetProgramCheckoutState(state) {
      return initialState;
    },
  },
});

export const {
  setSessionData,
  setLoadingCreateSession,
  setLoadingProcessPayment,
  setError,
  setDiscountApplied,
  removeDiscount,
  setAdminDiscountApplied,
  removeAdminDiscount,
  setPurchaseCompleted,
  resetProgramCheckoutState,
} = programCheckoutSlice.actions;

export default programCheckoutSlice.reducer;
